import React, { useState, useEffect } from 'react';
import { FaSearch, FaUser } from 'react-icons/fa';
import useSearchLeads from 'pages/Lead/hooks/useSearchLeads';
import LeadsSearchModal from '../LeadSearchModal';
import { LeadApiResponse } from 'models/Lead';

import './LeadsGlobalSearch.styles.scss';

const LeadsGlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);

  // ⏳ Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10); // renamed from pageSize

  // 🧠 Fetch leads with React Query including page & limit
  const { data, isFetching } = useSearchLeads({
    email: debouncedSearch,
    page: currentPage,
    limit,
  });

  // Only open modal when search starts
  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length > 0) {
      setIsSearchDialogOpen(true);
    }
  }, [debouncedSearch]);

  // Handlers for pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page); // React Query refetches automatically
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // reset to first page
  };

  return (
    <div className="search-bar">
      <FaSearch className="search-bar__icon search-bar__icon--main" />
      <FaUser className="search-bar__icon search-bar__icon--secondary" />

      <input
        name="global-search"
        type="text"
        placeholder="Search leads by email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="search-bar__input"
      />

      {debouncedSearch && (
        <span className="search-bar__badge">
          {!isFetching ? (
            data?.items?.length
          ) : (
            <div className="loading-spinner" />
          )}
        </span>
      )}

      {isSearchDialogOpen && (
        <LeadsSearchModal
          isSearchDialogOpen={isSearchDialogOpen}
          setIsSearchDialogOpen={() => {
            setSearchQuery('');
            setIsSearchDialogOpen(false);
          }}
          totalItems={data?.totalItems}
          totalPages={data?.totalPages}
          currentPage={currentPage}
          startIndex={(currentPage - 1) * limit + 1}
          endIndex={Math.min(currentPage * limit, data?.totalItems)}
          displayedItems={data?.items as LeadApiResponse[]}
          pageSize={limit}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handleLimitChange}
          isLoading={isFetching}
          modalSearchQuery={searchQuery}
          setModalSearchQuery={setSearchQuery}
          clearModalSearch={() => setSearchQuery('')}
          debouncedModalSearch={debouncedSearch}
        />
      )}
    </div>
  );
};

export default LeadsGlobalSearch;
